import {Component, HostListener, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {Store} from "@ngrx/store";
import {RestaurantTable} from "../../../@domain/restaurent-table";
import {firstValueFrom, forkJoin} from "rxjs";
import {CanComponentDeactivate} from "../../../../../projects/vendors/src/lib/guard/can-deactivate.guard";
import {ApiBaseService} from "../../../../../projects/vendors/src/lib/services/api-base.service";
import {SectionNamePipe} from "../../../../../projects/vendors/src/lib/pipe/section-name.pipe";
import {ApiEnum} from "../../../@application/enum/api.enum";
import * as fromApp from '../../../app.reducer'
import {CartRequestDetails} from "../../../@domain/cart-request-details";
import {OrderFoods} from "../../../@domain/order-foods";
import {Section} from "../../../@domain/section";
import {Order} from "../../../@domain/order";
import {ADD_PRINTED_FOODS_TO_CART, CLEAR_CART} from "../store/place-order/place-order.action";
import {getMenuState, getPlaceOrderState} from "../../../app.reducer";
import {MatDialog} from '@angular/material/dialog';
import {OrderCartDialogComponent} from './order-menu/order-cart-dialog/order-cart-dialog.component';
import {PathEnum} from "../../../@application/enum/path.enum";
import {SET_MENU_RELATED_DATA} from "../store/menu/menu.action";
import {ActivePos} from "../../../@domain/active-pos";
import {FoodItemQuantity} from "../../../@domain/food-item-quantity";
import {BaseComponent} from "../../../@application/component/base.component";
import {HttpErrorResponse, HttpParams} from "@angular/common/http";
import {ItemCategory} from "../../../@domain/item-category";
import {FoodItem} from "../../../@domain/food-item";
import {NgxSpinnerService} from "ngx-spinner";
import {NzModalService} from "ng-zorro-antd/modal";
import {AlertService} from "../../../../../projects/vendors/src/lib/services/alert.service";
import {KeyboardShortcutsDialogComponent} from '../../../../../projects/vendors/src/lib/components/keyboard-shortcuts-dialog/keyboard-shortcuts-dialog.component';
import {ResponsiveService} from "../../../@application/services/responsive.service";

@Component({
    selector: 'lib-place-order',
    templateUrl: './place-order.component.html',
    styleUrl: './place-order.component.css',
    standalone: false
})
export class PlaceOrderComponent extends BaseComponent implements OnInit, CanComponentDeactivate, OnDestroy {

  private apiBaseService = inject(ApiBaseService)
  private activatedRoute = inject(ActivatedRoute)
  private dialog = inject(MatDialog)
  private router = inject(Router)
  private location = inject(Location)
  private ngxLoader = inject(NgxSpinnerService)
  private modal = inject(NzModalService)
  private alertService = inject(AlertService)
  private store = inject(Store<fromApp.AppState>)
  private responsiveService = inject(ResponsiveService)

  orderId !: string
  isFoodPrinted = false
  printedFoods: CartRequestDetails[] = []
  orderDetails = {} as OrderFoods
  lastValidTables: string[] = []
  private revertingTableSelection = false
  tables = [] as RestaurantTable[];
  availableTables = [] as RestaurantTable[];
  sections = [] as Section[];
  sectionNamePipe = inject(SectionNamePipe)
  sectionId !: string
  isOrderPrinted!: boolean
  foodCount = 0

  // Track if we're in tablet/mobile view
  isTabletView = false;

  // Desktop cart visibility toggle (only used on desktop)
  desktopCartVisible = true;

  async ngOnInit() {
    // Clear cart from previous component (e.g., POS) to ensure clean state
    this.store.dispatch(CLEAR_CART());

    // Subscribe to breakpoint changes
    this.subscription$.add(
      this.responsiveService.isTablet$
        .subscribe(matches => {
          this.isTabletView = matches;
          // Reset desktop cart to visible when switching to desktop
          if (!this.isTabletView) {
            this.desktopCartVisible = true;
          }
        })
    )

    this.loadAllData()
    this.activatedRoute.queryParams.subscribe({
      next: (value: any) => {
        this.orderId = value.orderId
      }
    })
    this.subscription$.add(
      this.store.select(getPlaceOrderState)
        .subscribe({
          next: value => {
            this.foodCount = value.cartFoods.reduce((sum, item) => sum + item.quantity, 0);
          }
        })
    )
    await this.loadCartFoods()
  }

  private async loadCartFoods(): Promise<void> {
    try {
      const res = await firstValueFrom(this.apiBaseService.GET<OrderFoods[]>([ApiEnum.RestaurantModule, ApiEnum.Cart, ApiEnum.Order, this.orderId], false));
      this.isFoodPrinted = res.length > 0;
      if (this.isFoodPrinted) {
        this.orderDetails = res[0];
        this.sectionId = this.orderDetails.sectionId;
        this.lastValidTables = [...(this.orderDetails.tables ?? [])];
        const menuFoods = await this.getMenuFoods();
        this.printedFoods = res[0].cartResponseDetails.map(food => ({
          id: food.id,
          foodId: food.foodId,
          name: menuFoods.find(menuFood => menuFood.foodId === food.foodId)?.name,
          menuId: food.menuId,
          kitchenId: food.kitchenId,
          price: food.totalPrice,
          quantity: food.quantity,
          kitchenIntroduction: food.kitchenIntroduction,
          extras: food.extras,
          foodQuantityTagId: food.foodQuantityTagId,
          totalCost: 0,
          cartStatus: food.cartStatus,
          isServiceCharge: food.isServiceCharge,
        }));
        this.store.dispatch(ADD_PRINTED_FOODS_TO_CART({ payload: this.printedFoods }));
      } else {
        this.getOrder();
      }
    } catch (err) {
      console.error('Failed to load cart foods:', err);
    }
  }


  private getOrder(): void {
    this.apiBaseService.GET<Order>([ApiEnum.RestaurantModule, ApiEnum.Order, this.orderId], false)
      .subscribe({
        next: res => {
          this.orderDetails.orderId = res.id
          this.sectionId = res.sectionId
          this.orderDetails.sectionName = this.sectionNamePipe.transform(res.sectionId, this.sections)
          this.orderDetails.tables = res.tables
          this.lastValidTables = [...(this.orderDetails.tables ?? [])]
        }
      })
  }

  private async getMenuFoods(): Promise<{ foodId: string; name: string }[]> {
    const value = await firstValueFrom(this.store.select(getMenuState));
    return value.menuRelatedData.activePos.map(food => ({
      foodId: food.foodId,
      name: food.foodName
    }));
  }


  private loadAllData(isLoaderOn: boolean = false): void {
    if (isLoaderOn) {
      this.ngxLoader.show().then();
    }
    const sectionRequest = this.apiBaseService.GET<Section[]>([ApiEnum.RestaurantModule, ApiEnum.Section], false);
    const quantityRequest = this.apiBaseService.GET<FoodItemQuantity[]>([ApiEnum.FoodModule, ApiEnum.FoodItemQuantityTag], false);

    const posFilter = `isAvailableForAdmin eq true`;
    const posParams = new HttpParams().set('$filter', posFilter);
    const activePosRequest = this.apiBaseService.GET<ActivePos[]>([ApiEnum.FoodModule, ApiEnum.MenuGroup, ApiEnum.ActivePos], false, true, posParams);

    const itemCategoryRequest = this.apiBaseService.GET<ItemCategory[]>([ApiEnum.FoodModule, ApiEnum.FoodItemCategory], false);

    const foodParams = new HttpParams().append('$select', 'id,basePrice,marginPercentage,name,kitchenId,foodCategoryName,foodCategoryId');
    const foodItemRequest = this.apiBaseService.GET<FoodItem[]>([ApiEnum.FoodModule, ApiEnum.Food], false, true, foodParams);

    const tableRequest = this.apiBaseService.GET<RestaurantTable[]>([ApiEnum.RestaurantModule, ApiEnum.RestaurantTable], false);

    forkJoin({
      sections: sectionRequest,
      quantities: quantityRequest,
      activePos: activePosRequest,
      itemCategories: itemCategoryRequest,
      foodItems: foodItemRequest,
      tables: tableRequest
    }).subscribe({
      next: ({sections, quantities, activePos, itemCategories, foodItems,tables}) => {
        this.sections = sections;
        this.tables = tables;
        this.availableTables = tables.filter(table => table.availabilityStatus === 1 && table.status === 1);
        const thisOrderTables = this.tables.filter(table => this.orderDetails.tables?.some(ot => ot === table.id));
        this.availableTables = [...this.availableTables, ...thisOrderTables];
        this.store.dispatch(SET_MENU_RELATED_DATA({
          payload: {
            foodItem: foodItems,
            quantity: quantities,
            section: sections,
            category: itemCategories,
            activePos: activePos
          }
        }))
        if (isLoaderOn) {
          this.ngxLoader.hide().then();
        }
      },
      error: err => {
        console.error('Failed to load one or more resources:', err);
      }
    });
  }


  async onFoodSubmitted(foodSubmitted: boolean) {
    if (foodSubmitted) {
      await this.loadCartFoods();
      this.router.navigate([PathEnum.Home, PathEnum.TableOccupancy]).then(() => {});
    }
  }

  onOrderDetailsUpdated(order: OrderFoods) {
    this.orderDetails = order;
    this.sectionId = order.sectionId;
    this.lastValidTables = [...(order.tables ?? [])];

    const available = this.tables.filter(table => table.availabilityStatus === 1 && table.status === 1);
    const orderTables = this.tables.filter(table => this.lastValidTables.some(id => id === table.id));
    const mergedTables = [...available];
    orderTables.forEach(table => {
      if (!mergedTables.some(existing => existing.id === table.id)) {
        mergedTables.push(table);
      }
    });
    this.availableTables = mergedTables;
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (!this.isOrderPrinted) {
      return new Promise<boolean>((resolve) => {
        this.modal.confirm({
          nzTitle: 'Unsaved Changes',
          nzContent: 'You have unsaved changes. Do you really want to leave?',
          nzOkText: 'Leave',
          nzCancelText: 'Stay',
          nzOkDanger: true,
          nzOnOk: () => resolve(true),
          nzOnCancel: () => resolve(false)
        });
      });
    }
    return true
  }

  toggleOrderCart() {
    if (this.isTabletView) {
      // On tablet/mobile, ALWAYS open dialog
      this.openCartDialog();
    } else {
      // On desktop, toggle cart visibility
      this.desktopCartVisible = !this.desktopCartVisible;
    }
  }

  getCartTooltip(): string {
    if (this.isTabletView) {
      return 'View Cart';
    }
    return this.desktopCartVisible ? 'Hide Cart' : 'Show Cart';
  }

  openCartDialog() {
    const dialogRef = this.dialog.open(OrderCartDialogComponent, {
      width: '90%',
      height: '90vh',
      panelClass: 'cart-dialog',
      data: {
        orderId: this.orderId,
        orderDetails: this.orderDetails
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Order created') {
        this.isOrderPrinted = true
        this.onFoodSubmitted(true)
      }
    });
  }

  onOrderCart() {
    this.openCartDialog();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Check if cancel order modal is open
    const cancelModal = document.querySelector('.ant-modal-confirm-warning');
    if (cancelModal) {
      // Enter - Confirm cancel order
      if (event.key === 'Enter') {
        event.preventDefault();
        const okButton = cancelModal.querySelector('.ant-btn-primary') as HTMLButtonElement;
        okButton?.click();
        return;
      }
      // Escape - Close cancel order modal
      if (event.key === 'Escape') {
        event.preventDefault();
        const cancelButton = cancelModal.querySelector('.ant-modal-confirm-btns .ant-btn:not(.ant-btn-primary)') as HTMLButtonElement;
        cancelButton?.click();
        return;
      }
      return; // Don't process other shortcuts while modal is open
    }

    // Skip if user is typing in an input field (except for ? key which opens help)
    const activeElement = document.activeElement;
    const isInputFocused = activeElement instanceof HTMLInputElement ||
                           activeElement instanceof HTMLTextAreaElement;

    // ? - Show keyboard shortcuts (works even in input fields)
    if (event.key === '?' || (event.shiftKey && event.key === '/')) {
      event.preventDefault();
      this.showKeyboardShortcuts();
      return;
    }

    // Skip if other dialogs are open
    const dialogOpen = document.querySelector('.cdk-overlay-pane mat-dialog-container, .ant-modal-wrap:not(.ant-modal-confirm)');
    if (dialogOpen) {
      return;
    }

    if (isInputFocused) return;

    // Backspace - Go back to previous route
    if (event.key === 'Backspace') {
      event.preventDefault();
      this.location.back();
      return;
    }

    // Escape - Cancel Order
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancelOrder();
      return;
    }
  }


  onNotPrinted(isOrderPrinted: boolean) {
    this.isOrderPrinted = isOrderPrinted
  }

  showKeyboardShortcuts() {
    this.dialog.open(KeyboardShortcutsDialogComponent, {
      width: '560px',
      autoFocus: false,
      disableClose: false
    });
  }

  onNewItem(event: boolean) {
    if (event)
      this.loadAllData(true);
  }

  onCancelOrder() {
    this.modal.warning({
      nzTitle: 'Warning',
      nzContent: 'Are you sure to cancel this order?',
      nzOkText: 'Yes',
      nzOnOk: async () => {
        this.deleteOrder()
      }
    });
  }

  private deleteOrder(): void {
    this.apiBaseService.PATCH_API([ApiEnum.RestaurantModule, ApiEnum.Order, ApiEnum.Delete],
      {
        orderId: this.orderId,
        deleteReason: 'By Mistake'
      }, true, 'Order Deleted Successfully').subscribe({
      next: value => {
        this.isOrderPrinted = true
        this.router.navigate([PathEnum.Home, PathEnum.TableOccupancy]).then(() => {});
      },
      error: (err: HttpErrorResponse)  => {
        if (err.status === 403) {
          this.alertService.showInfo('Info','please use admin credentials to delete order');
        }
      }
    })
  }

  onTableChange(tables: string[]) {
    if (this.revertingTableSelection) {
      this.revertingTableSelection = false;
      return;
    }

    const newSelection = tables ?? [];
    if (newSelection.length === 0) {
      this.alertService.showInfo('Info','At least one table must be selected');
      this.revertingTableSelection = true;
      this.orderDetails.tables = [...this.lastValidTables];
      return;
    }

    const prevSelection = [...this.lastValidTables];
    this.lastValidTables = [...newSelection];

    this.apiBaseService.UPDATE_API([ApiEnum.RestaurantModule,ApiEnum.Table,ApiEnum.MoveOrder],{
      orderId: this.orderId,
      tableIds: newSelection
    }).subscribe({
      next: _value => {
        this.getOrder()
        // Successfully updated on server; nothing else to do
      },
      error: _err => {
        this.alertService.showInfo('Info','Failed to update tables, reverting selection');
        this.revertingTableSelection = true;
        this.orderDetails.tables = prevSelection;
        this.lastValidTables = prevSelection;
      }
    })
  }
}
